import { useState, useRef, useEffect, type KeyboardEvent } from "react";
import { SendHorizonal, StopCircle, Mic, MicOff, AudioLines, Paperclip, X, Loader2, AtSign, FileCode, Image as ImageIcon, Sparkles } from "lucide-react";
import { cn } from "../../lib/cn";
import { useVoice } from "../../hooks/useVoice";
import { ConversationOverlay } from "./ConversationOverlay";
import { unlockAudio } from "../../lib/globalTts";
import { getBaseUrl, getToken } from "../../lib/storage";
import { analyzeImage, recognizeImage } from "../../lib/api";

interface AttachedFile {
  id: string;                    // 9.10-bugfix: unique id чтобы избежать race conditions с idx
  name: string;
  size: number;
  fileId?: string;
  preview?: string;
  fullMarkdown?: string;         // полный текст из файла (для контекста в сообщении)
  uploading?: boolean;
  error?: string;
  // Vision (P0-3)
  isImage?: boolean;
  imageDataUrl?: string;        // для preview (base64)
  visionDescription?: string;   // результат /vision/analyze (если выполнен)
  visionAnalyzing?: boolean;
}

// Локальное хранилище оригиналов File для повторных вызовов analyzeImage.
// Не пишем в state, чтобы не переживать сериализацию File. Ключ — attachment.id (уникален).
const pendingImageFiles = new Map<string, File>();

function makeAttachmentId(): string {
  // crypto.randomUUID() поддерживается всеми современными браузерами + Node 19+.
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  // Fallback для старых движков
  return `att-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

export function InputArea({
  onSend,
  onCancel,
  disabled,
  streaming,
}: {
  onSend: (text: string) => void;
  onCancel?: () => void;
  disabled?: boolean;
  streaming?: boolean;
}) {
  const [value, setValue] = useState("");
  const [attached, setAttached] = useState<AttachedFile[]>([]);
  const [dragOver, setDragOver] = useState(false);
  const [conversationOpen, setConversationOpen] = useState(false);
  const [convState, setConvState] = useState<"idle" | "listening" | "thinking" | "speaking">("idle");
  const fileInputRef = useRef<HTMLInputElement>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);

  const { state: voiceState, supported, startListening, stopListening } = useVoice({
    onTranscript: (text) => {
      setValue((v) => (v ? v + " " + text : text));
    },
  });

  // Слушаем prefill из Welcome / других мест: window.dispatchEvent(new CustomEvent('aicores:prompt', { detail: '...' }))
  // Не отправляем автоматически — пользователь видит подставленный текст и может его доредактировать.
  useEffect(() => {
    function onPick(e: Event) {
      const detail = (e as CustomEvent<string>).detail;
      if (typeof detail !== "string" || !detail.trim()) return;
      setValue(detail);
      // Дать React отрендерить, потом сфокусировать textarea (если она в DOM)
      setTimeout(() => {
        const ta = document.querySelector<HTMLTextAreaElement>(
          "textarea[data-aicores-input]",
        );
        if (ta) {
          ta.focus();
          ta.setSelectionRange(detail.length, detail.length);
        }
      }, 30);
    }
    window.addEventListener("aicores:prompt", onPick as EventListener);
    return () => window.removeEventListener("aicores:prompt", onPick as EventListener);
  }, []);

  async function uploadFile(file: File): Promise<AttachedFile> {
    const id = makeAttachmentId();
    const placeholder: AttachedFile = {
      id,
      name: file.name,
      size: file.size,
      uploading: true,
    };
    setAttached((arr) => [...arr, placeholder]);
    try {
      const formData = new FormData();
      // ВАЖНО: backend `/api/v1/files/upload` ожидает поле `files` (множественное число),
      // т.к. сигнатура — `files: list[UploadFile] = File(...)`. Раньше отправляли `file`,
      // получали 422 «Field required», и аплоад тихо ломался.
      formData.append("files", file);
      const r = await fetch(`${getBaseUrl()}/api/v1/files/upload`, {
        method: "POST",
        headers: { Authorization: `Bearer ${getToken()}` },
        body: formData,
      });
      if (!r.ok) {
        let detail = "";
        try {
          const errBody = await r.json();
          detail = (errBody as Record<string, unknown>).detail as string || JSON.stringify(errBody);
        } catch {
          detail = await r.text().catch(() => "");
        }
        throw new Error(detail || `HTTP ${r.status}`);
      }
      // Backend возвращает {success: bool, files: [{file_id, name, markdown, markdown_excerpt, ...}]}
      const data = (await r.json()) as {
        success?: boolean;
        files?: Array<{
          file_id?: string;
          name?: string;
          markdown?: string | null;
          markdown_excerpt?: string | null;
          kind?: string;
        }>;
      };
      const first = data.files?.[0];
      if (!first?.file_id) {
        throw new Error("Сервер не вернул file_id");
      }
      // Используем полный markdown (если есть) для контекста — `markdown_excerpt`
      // обрезается до ~500 символов и теряет суть документа.
      const fullMd = (first.markdown || first.markdown_excerpt || "").trim();
      const result: AttachedFile = {
        id,
        name: first.name || file.name,
        size: file.size,
        fileId: first.file_id,
        // preview оставляем как короткий чип, fullMarkdown пишем в отдельное поле
        preview: fullMd.slice(0, 200),
        fullMarkdown: fullMd,
      };
      setAttached((arr) => arr.map((a) => (a.id === id ? result : a)));
      return result;
    } catch (e) {
      // 9.10-bugfix: логируем в консоль для быстрой диагностики, не только в UI.
      // eslint-disable-next-line no-console
      console.error(`[InputArea.uploadFile] ${file.name}:`, e);
      const result: AttachedFile = {
        id,
        name: file.name,
        size: file.size,
        error: e instanceof Error ? e.message : "Ошибка загрузки",
      };
      setAttached((arr) => arr.map((a) => (a.id === id ? result : a)));
      return result;
    }
  }

  function handleFiles(files: FileList | null) {
    if (!files || files.length === 0) return;
    Array.from(files).forEach((f) => {
      if (f.type.startsWith("image/")) {
        addImage(f);
      } else {
        void uploadFile(f);
      }
    });
  }

  /**
   * Добавляет изображение для preview. Анализ через vision запускается
   * автоматически в фоне (раньше требовался ручной клик «Распознать» — это была
   * скрытая функциональность, пользователи не догадывались).
   */
  function addImage(file: File) {
    const id = makeAttachmentId();
    pendingImageFiles.set(id, file);
    // URL.createObjectURL надёжнее FileReader (не требует base64 в памяти,
    // не падает на больших файлах, мгновенно возвращает blob: URL).
    let imageUrl = "";
    try {
      imageUrl = URL.createObjectURL(file);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(`[InputArea.addImage] createObjectURL failed for ${file.name}`, e);
    }
    setAttached((arr) => [
      ...arr,
      {
        id,
        name: file.name,
        size: file.size,
        isImage: true,
        imageDataUrl: imageUrl,
        ...(imageUrl ? {} : { error: "Не удалось прочитать файл" }),
      },
    ]);
    if (imageUrl) {
      // Автозапуск vision-анализа — отправит на /vision/analyze для распознавания.
      void runVision(id);
    } else {
      pendingImageFiles.delete(id);
    }
  }

  async function runVision(id: string) {
    const item = attached.find((a) => a.id === id);
    if (!item || !item.isImage) return;
    const file = pendingImageFiles.get(id);
    if (!file) return;
    setAttached((arr) =>
      arr.map((a) => (a.id === id ? { ...a, visionAnalyzing: true, error: undefined } : a)),
    );
    // 1) Сначала пробуем локальный OCR (rapidocr-onnxruntime в aicores-api).
    //    Бесплатно, оффлайн, без сторонних ключей. Покрывает 90% запросов
    //    «прочитай текст со скрина».
    try {
      const ocr = await recognizeImage(file);
      setAttached((arr) =>
        arr.map((a) =>
          a.id === id
            ? {
                ...a,
                visionAnalyzing: false,
                visionDescription: ocr.description,
                fileId: ocr.fileId,
              }
            : a,
        ),
      );
      return;
    } catch (ocrErr) {
      // OCR не справился (нет текста или сервис лежит) — попробуем
      // полноценный vision-LLM, если у пользователя есть ключ. Это даёт
      // и описание картинки, и распознавание сцены/UI/диаграмм.
      // eslint-disable-next-line no-console
      console.info(`[InputArea.runVision] OCR fallback for ${item.name}:`, ocrErr);
    }
    try {
      const result = await analyzeImage(file, {
        prompt: "Опиши подробно что на изображении. Если код — приведи текст.",
        provider: "openai",
      });
      setAttached((arr) =>
        arr.map((a) =>
          a.id === id
            ? { ...a, visionAnalyzing: false, visionDescription: result.description }
            : a,
        ),
      );
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(`[InputArea.runVision] ${item.name}:`, e);
      const msg = e instanceof Error ? e.message : "Ошибка анализа";
      setAttached((arr) =>
        arr.map((a) => (a.id === id ? { ...a, visionAnalyzing: false, error: msg } : a)),
      );
    }
  }

  function removeAttached(id: string) {
    // Освобождаем blob: URL
    setAttached((arr) => {
      const found = arr.find((a) => a.id === id);
      if (found?.imageDataUrl?.startsWith("blob:")) {
        try { URL.revokeObjectURL(found.imageDataUrl); } catch {}
      }
      return arr.filter((a) => a.id !== id);
    });
    pendingImageFiles.delete(id);
    return;
  }

  // Drag-drop handlers
  function onDragOver(e: React.DragEvent) {
    e.preventDefault();
    setDragOver(true);
  }
  function onDragLeave() {
    setDragOver(false);
  }
  function onDrop(e: React.DragEvent) {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files?.length) handleFiles(e.dataTransfer.files);
  }

  const submit = () => {
    if ((!value.trim() && attached.length === 0) || disabled) return;
    let messageText = value.trim();
    // Файлы — file_id для агента + полный markdown как контекст.
    const files = attached.filter((a) => a.fileId && !a.error);
    if (files.length > 0) {
      const fileLines = files
        .map((f) => {
          const head = `📎 ${f.name} (file_id: ${f.fileId})`;
          // Полный markdown: ограничиваем до ~12k символов (≈3k токенов) чтобы не
          // переполнить контекст на больших PDF/DOCX. Если файл больше — обрезаем.
          const md = (f.fullMarkdown || f.preview || "").trim();
          if (!md) return head;
          const limited = md.length > 12000 ? md.slice(0, 12000) + "\n\n…(truncated)" : md;
          return `${head}\n\n${limited}`;
        })
        .join("\n\n---\n\n");
      messageText = (messageText + "\n\n" + fileLines).trim();
    }
    // Vision-описания изображений — добавляются как контекст.
    const images = attached.filter((a) => a.isImage && a.visionDescription);
    if (images.length > 0) {
      const imgLines = images
        .map((i) => `🖼 ${i.name}:\n${i.visionDescription}`)
        .join("\n\n");
      messageText = (messageText + "\n\n" + imgLines).trim();
    }
    onSend(messageText);
    setValue("");
    pendingImageFiles.clear();
    setAttached([]);
  };

  const onKey = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && (e.metaKey || e.ctrlKey || !e.shiftKey)) {
      e.preventDefault();
      submit();
    }
  };

  const isListening = voiceState === "listening";
  const hasUploading = attached.some((a) => a.uploading || a.visionAnalyzing);

  return (
    <div
      className="space-y-2 relative"
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
    >
      {/* Drop-overlay при перетаскивании файла/изображения */}
      {dragOver && (
        <div className="absolute inset-0 z-10 rounded-card border-2 border-dashed border-accent-500 bg-accent-500/10 backdrop-blur-sm flex items-center justify-center pointer-events-none">
          <div className="flex items-center gap-2 text-accent-300 font-medium">
            <ImageIcon className="size-5" />
            Отпустите, чтобы прикрепить
          </div>
        </div>
      )}

      {/* Прикреплённые файлы и изображения */}
      {attached.length > 0 && (
        <div className="flex flex-wrap gap-2 px-1">
          {attached.map((f) =>
            f.isImage ? (
              <ImageChip key={f.id} f={f} onRemove={removeAttached} onAnalyze={runVision} />
            ) : (
              <FileChip key={f.id} f={f} onRemove={removeAttached} />
            ),
          )}
        </div>
      )}

      <div className="rounded-modal flex items-end gap-0.5 sm:gap-1 pl-1 pr-2 sm:pr-2.5 py-2 relative bg-bg-card border border-border/60 shadow-soft transition-all focus-within:border-accent-500/50 focus-within:shadow-glow">
        {/* Кнопка прикрепления файла */}
        {!streaming && (
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={disabled}
            className="btn-ghost text-fg-muted hover:text-accent-400"
            aria-label="Прикрепить файл"
            title="Прикрепить файл (PDF, изображение, документ)"
          >
            <Paperclip className="size-5" />
          </button>
        )}
        <input
          ref={fileInputRef}
          type="file"
          multiple
          className="hidden"
          accept=".pdf,.txt,.md,.json,.csv,.docx,.xlsx,.pptx,.png,.jpg,.jpeg,.webp,.gif"
          onChange={(e) => handleFiles(e.target.files)}
        />

        <textarea
          data-aicores-input
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={onKey}
          placeholder="Спросите AICORES…"
          rows={1}
          className={cn(
            "flex-1 resize-none bg-transparent outline-none px-1 sm:px-2 py-2",
            "text-body placeholder:text-fg-subtle min-h-[3.5rem] max-h-[14rem]",
          )}
          disabled={disabled}
        />

        {/* Кнопка conversation-mode (полноценный голосовой диалог: STT → agent → TTS).
            На мобиле тоже показываем (после жалоб пользователей). */}
        {!streaming && (
          <button
            type="button"
            onClick={() => {
              if (conversationOpen) { setConversationOpen(false); setConvState("idle"); }
              else { unlockAudio(); setConversationOpen(true); }
            }}
            className={cn(
              "flex items-center gap-1.5 rounded-full px-2.5 py-1.5 transition-colors",
              conversationOpen
                ? "bg-accent-500/15 text-accent-400"
                : "text-fg-muted hover:text-fg",
            )}
            aria-label="Голосовой режим"
            title={conversationOpen ? "Выйти из голосового режима" : "Голосовой режим"}
          >
            {conversationOpen ? (
              <>
                {/* Мигающая точка = идёт запись/диалог, рядом понятная подпись */}
                <span className="relative flex size-2.5">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-accent-400 opacity-75" />
                  <span className="relative inline-flex size-2.5 rounded-full bg-accent-500" />
                </span>
                <span className="text-xs font-medium whitespace-nowrap">
                  {convState === "listening" ? "Слушаю"
                    : convState === "thinking" ? "Думаю"
                    : convState === "speaking" ? "Отвечаю"
                    : "Запись"}
                </span>
              </>
            ) : (
              <Mic className="size-5" />
            )}
          </button>
        )}

        {streaming ? (
          <button
            type="button"
            onClick={onCancel}
            className="btn-ghost text-danger"
            aria-label="Прервать"
            title="Остановить генерацию"
          >
            <StopCircle className="size-5" />
          </button>
        ) : (
          <button
            type="button"
            onClick={submit}
            disabled={(!value.trim() && attached.length === 0) || disabled || hasUploading}
            className="relative size-11 sm:size-9 grid place-items-center rounded-xl text-white shadow-accent transition-all hover:-translate-y-0.5 active:translate-y-0 disabled:opacity-40 disabled:translate-y-0 disabled:cursor-not-allowed disabled:shadow-none"
            style={{ backgroundImage: "var(--grad-cta)" }}
            aria-label="Отправить"
            title={hasUploading ? "Дождитесь загрузки файлов" : "Отправить (Enter)"}
          >
            <SendHorizonal className="size-4" />
          </button>
        )}
      </div>
      {conversationOpen && (
        <ConversationOverlay onClose={() => { setConversationOpen(false); setConvState("idle"); }} onState={setConvState} />
      )}
    </div>
  );
}


/**
 * MentionPicker — кнопка @, открывает popup с быстрым выбором файла из workspace.
 * Выбранный файл вставляется в input как `@file:path/to/file` — backend
 * автоматически разворачивает в read_file как контекст.
 *
 * Пока поддерживает только @file. Будущие итерации:
 *  - @folder (показать дерево из workspace/tree)
 *  - @web url (вытащить контент через urlfetch)
 *  - @diff / @problems / @selection
 */
function MentionPicker({
  value,
  setValue,
  disabled,
}: {
  value: string;
  setValue: (v: string) => void;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [path, setPath] = useState("");

  // Закрытие на Esc
  useEffect(() => {
    if (!open) return;
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  function insertMention() {
    const p = path.trim();
    if (!p) return;
    const sep = value.endsWith(" ") || value.length === 0 ? "" : " ";
    setValue(`${value}${sep}@file:${p} `);
    setPath("");
    setOpen(false);
  }

  return (
    <>
      <button
        type="button"
        onClick={() => !disabled && setOpen(!open)}
        disabled={disabled}
        className="btn-ghost text-fg-muted hover:text-accent-400"
        aria-label="Упомянуть файл"
        title="@-упоминание (файл / папка / контекст)"
      >
        <AtSign className="size-5" />
      </button>

      {open && (
        <div className="absolute bottom-full left-0 mb-2 w-80 card p-3 shadow-2xl border border-border/40 z-20">
          <div className="text-small font-medium mb-2 flex items-center gap-1.5">
            <FileCode className="size-3.5 text-accent-400" />
            Упомянуть файл
          </div>
          <input
            type="text"
            value={path}
            onChange={(e) => setPath(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                insertMention();
              }
            }}
            placeholder="например, app/main.py"
            className="input w-full text-small"
            autoFocus
          />
          <div className="text-[0.75rem] text-fg-muted mt-2">
            Файл будет добавлен как контекст в сообщение: <code className="bg-bg-subtle px-1 rounded">@file:path</code>
          </div>
          <div className="flex gap-2 mt-3 justify-end">
            <button
              onClick={() => setOpen(false)}
              className="btn-ghost text-small"
            >
              Отмена
            </button>
            <button
              onClick={insertMention}
              disabled={!path.trim()}
              className="btn-primary text-small"
            >
              Вставить
            </button>
          </div>
        </div>
      )}
    </>
  );
}


// === Chip helpers ===

function FileChip({
  f,
  onRemove,
}: {
  f: AttachedFile;
  onRemove: (id: string) => void;
}) {
  return (
    <div
      className={cn(
        "flex items-center gap-2 px-3 py-1.5 rounded-card border text-small",
        f.error
          ? "border-yellow-500/40 bg-yellow-500/10 text-yellow-300"
          : f.uploading
          ? "border-border bg-bg-subtle"
          : "border-accent-500/30 bg-accent-500/5",
      )}
      title={f.error ? `Не удалось загрузить: ${f.error}` : `${(f.size / 1024).toFixed(1)} KB`}
    >
      {f.uploading ? (
        <Loader2 className="size-3.5 animate-spin shrink-0" />
      ) : f.error ? (
        <span className="text-[1em] shrink-0">⚠️</span>
      ) : (
        <Paperclip className="size-3.5 shrink-0" />
      )}
      <span className="truncate max-w-[200px]">{f.name}</span>
      <span className="text-fg-subtle text-[0.85em] shrink-0">
        {f.error ? "ошибка" : `${(f.size / 1024).toFixed(0)}KB`}
      </span>
      <button
        type="button"
        onClick={() => onRemove(f.id)}
        className="text-fg-muted hover:text-fg shrink-0"
        aria-label="Удалить"
      >
        <X className="size-3.5" />
      </button>
    </div>
  );
}

function ImageChip({
  f,
  onRemove,
  onAnalyze,
}: {
  f: AttachedFile;
  onRemove: (id: string) => void;
  onAnalyze: (id: string) => void;
}) {
  const analyzed = Boolean(f.visionDescription);
  return (
    <div
      className={cn(
        "relative rounded-card border overflow-hidden",
        f.error
          ? "border-yellow-500/40 bg-yellow-500/10"
          : analyzed
          ? "border-green-500/40 bg-green-500/5"
          : "border-accent-500/30 bg-accent-500/5",
      )}
      title={f.error || (analyzed ? "Vision: распознано" : f.name)}
    >
      {f.imageDataUrl && (
        <img
          src={f.imageDataUrl}
          alt={f.name}
          className="size-16 object-cover"
        />
      )}
      <div className="px-2 py-1 text-[0.7rem] flex items-center gap-1 max-w-[10rem]">
        <span className="truncate">{f.name}</span>
      </div>
      {/* Vision-analyze button */}
      {!analyzed && !f.visionAnalyzing && !f.error && (
        <button
          type="button"
          onClick={() => onAnalyze(f.id)}
          className="absolute bottom-7 left-1 right-1 px-1.5 py-0.5 rounded bg-accent-500/80 hover:bg-accent-500 text-white text-[0.65rem] flex items-center justify-center gap-1"
          title="Проанализировать через vision-LLM"
        >
          <Sparkles className="size-3" />
          Распознать
        </button>
      )}
      {f.visionAnalyzing && (
        <div className="absolute inset-0 grid place-items-center bg-black/40 backdrop-blur-sm">
          <Loader2 className="size-5 animate-spin text-white" />
        </div>
      )}
      {analyzed && (
        <div className="absolute top-1 left-1 px-1 py-0.5 rounded bg-green-500/90 text-[0.65rem] text-white flex items-center gap-1">
          <Sparkles className="size-2.5" />
          OK
        </div>
      )}
      {/* Remove */}
      <button
        type="button"
        onClick={() => onRemove(f.id)}
        className="absolute top-1 right-1 size-5 rounded-full bg-bg/80 hover:bg-red-500 text-fg-muted hover:text-white grid place-items-center"
        aria-label="Удалить"
      >
        <X className="size-3" />
      </button>
    </div>
  );
}
